import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Dialog } from '@pilot-hub/ui'
import { flightApi } from '@pilot-hub/api-client'
import { Loader2, Users } from 'lucide-react'
import type { TruppInfo, TruppPerson } from '../hooks/useEinsatzTrupps'
import { TRUPP_TYP_LABEL } from '../hooks/useEinsatzTrupps'

export type Rolle = 'pilot_id' | 'co_pilot_id' | 'beobachter_id'
const ROLLEN: { feld: Rolle; label: string; funktion: string }[] = [
  { feld: 'pilot_id', label: 'Pilot', funktion: 'PILOT' },
  { feld: 'co_pilot_id', label: 'Co-Pilot', funktion: 'CO_PILOT' },
  { feld: 'beobachter_id', label: 'Luftraumbeobachter', funktion: 'LUFTRAUM_BEOBACHTER' },
]
const FUNKTION_ZU_FELD: Record<string, Rolle> = { PILOT: 'pilot_id', CO_PILOT: 'co_pilot_id', LUFTRAUM_BEOBACHTER: 'beobachter_id' }

interface FlugKurz { id: number; flug_nr?: string | null; pilot_id?: number | null; co_pilot_id?: number | null; beobachter_id?: number | null }
type Zuordnung = Record<number, Rolle | null>   // Mitglied -> Rolle

/** Vorbelegung: 1. Funktion im Kräfte Management (nur wenn eindeutig), 2. Konstellation des letzten Flugs desselben Trupps. */
export function vorbelegung(trupp: TruppInfo, personen: Map<number, TruppPerson>, letzter: FlugKurz | null): Zuordnung {
  const darf = (id: number, feld: Rolle) => (personen.get(id)?.funktionen ?? []).includes(ROLLEN.find(r => r.feld === feld)!.funktion)
  const z: Zuordnung = Object.fromEntries(trupp.mitglieder.map(id => [id, null]))
  // 1. Kräfte Management: jede Rolle nur, wenn genau ein Mitglied sie dort hat
  for (const r of ROLLEN) {
    const kandidaten = trupp.mitglieder.filter(id => personen.get(id)?.kmFunktion === r.funktion && darf(id, r.feld))
    if (kandidaten.length === 1 && z[kandidaten[0]] == null) z[kandidaten[0]] = r.feld
  }
  // 2. letzter Flug desselben Trupps für alle noch nicht besetzten Rollen und Mitglieder ohne Rolle
  if (letzter) {
    for (const r of ROLLEN) {
      const id = letzter[r.feld]
      if (id != null && trupp.mitglieder.includes(id) && z[id] == null && !Object.values(z).includes(r.feld) && darf(id, r.feld)) z[id] = r.feld
    }
  }
  return z
}

export function TruppUebernehmen({ einsatzId, flug, trupps, personen, nutzerListe, vorgabeTruppKey, onFertig }: {
  einsatzId: number | null
  flug: FlugKurz & { beobachter_label?: string | null }
  trupps: TruppInfo[]
  personen: Map<number, TruppPerson>
  nutzerListe: Map<number, { name: string; funktionen: string[] }>
  vorgabeTruppKey: string | null
  onFertig: (werte: Record<Rolle, number | null>) => void
}) {
  const [offen, setOffen] = useState(false)
  const [truppKey, setTruppKey] = useState<string>('')
  const [zuordnung, setZuordnung] = useState<Zuordnung>({})
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const trupp = trupps.find(t => t.key === truppKey) ?? null
  const info = (id: number) => personen.get(id) ?? (nutzerListe.has(id) ? { id, name: nutzerListe.get(id)!.name, funktionen: nutzerListe.get(id)!.funktionen, kmFunktion: null, fahrzeug: null } : undefined)

  const oeffnen = () => {
    setFehler(null)
    setTruppKey(vorgabeTruppKey && trupps.some(t => t.key === vorgabeTruppKey) ? vorgabeTruppKey : trupps[0]?.key ?? '')
    setOffen(true)
  }

  // Vorbelegung, sobald ein Trupp gewählt ist (inkl. letztem Flug dieses Trupps im selben Einsatz)
  useEffect(() => {
    if (!offen || !trupp) return
    let abbruch = false
    ;(async () => {
      let letzter: FlugKurz | null = null
      try {
        const r = await flightApi.list({ einsatz_id: einsatzId ?? undefined, size: 100 } as any)
        const items = ((r.data?.items ?? []) as any[]).filter(f => f.id !== flug.id && f.trupp_besetzung?.truppKey === trupp.key)
        letzter = items[0] ?? null   // Liste ist nach Startzeit absteigend sortiert
      } catch { /* ohne letzten Flug vorbelegen */ }
      if (!abbruch) setZuordnung(vorbelegung(trupp, new Map([...personen].map(([k, v]) => [k, { ...v, funktionen: v.funktionen.length ? v.funktionen : (nutzerListe.get(k)?.funktionen ?? []) }])), letzter))
    })()
    return () => { abbruch = true }
  }, [offen, truppKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const setzen = (id: number, feld: Rolle | null) => {
    setZuordnung(z => {
      const neu = { ...z }
      if (feld) for (const k of Object.keys(neu)) if (neu[Number(k)] === feld) neu[Number(k)] = null   // jede Rolle nur einmal
      neu[id] = feld
      return neu
    })
  }

  const inhaber = (feld: Rolle) => { const e = Object.entries(zuordnung).find(([, v]) => v === feld); return e ? Number(e[0]) : null }
  const beobachterBleibt = inhaber('beobachter_id') == null && flug.beobachter_id != null
  const beobachterName = flug.beobachter_id != null ? (flug.beobachter_label || nutzerListe.get(flug.beobachter_id)?.name || `Benutzer ${flug.beobachter_id}`) : null
  const beobachterAusserhalb = flug.beobachter_id != null && !!trupp && !trupp.mitglieder.includes(flug.beobachter_id)
  const pilotId = inhaber('pilot_id')

  const bestaetigen = async () => {
    if (!pilotId) return
    setSpeichert(true); setFehler(null)
    try {
      const werte = {
        pilot_id: pilotId,
        co_pilot_id: inhaber('co_pilot_id'),
        beobachter_id: inhaber('beobachter_id') ?? flug.beobachter_id ?? null,
      }
      await flightApi.update(flug.id, werte)
      setOffen(false)
      onFertig(werte)
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen – bitte erneut versuchen.')
    } finally { setSpeichert(false) }
  }

  return (
    <>
      <Button variant="secondary" onClick={oeffnen} className="min-h-[44px]" data-testid="trupp-uebernehmen">
        <Users className="mr-2 h-4 w-4" />Trupp übernehmen
      </Button>
      {createPortal(<Dialog isOpen={offen} onClose={() => !speichert && setOffen(false)} title="Trupp übernehmen" maxWidth="lg"
        footer={<>
          <Button variant="secondary" onClick={() => setOffen(false)} disabled={speichert} className="min-h-[48px]">Abbrechen</Button>
          <Button onClick={bestaetigen} disabled={speichert || !pilotId} className="min-h-[48px]" data-testid="trupp-bestaetigen">
            {speichert ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Bestätigen'}
          </Button>
        </>}>
        <div className="space-y-3" data-testid="trupp-dialog">
          {trupps.length > 1 && (
            <label className="block text-sm text-dark-text-muted">Trupp
              <select value={truppKey} onChange={e => setTruppKey(e.target.value)} data-testid="trupp-wahl"
                className="mt-1 min-h-[44px] w-full rounded-lg border border-dark-border bg-dark-bg-tertiary px-3 text-base text-dark-text">
                {trupps.map(t => <option key={t.key} value={t.key}>{t.name} ({TRUPP_TYP_LABEL[t.typ] ?? t.typ})</option>)}
              </select>
            </label>
          )}
          {trupp && trupps.length === 1 && <p className="text-sm text-dark-text-muted">{trupp.name} · {TRUPP_TYP_LABEL[trupp.typ] ?? trupp.typ}</p>}
          {trupp && trupp.mitglieder.length === 0 && <p className="text-sm text-dark-text-muted">Dieser Trupp hat noch keine Mitglieder.</p>}
          {trupp?.mitglieder.map(id => {
            const p = info(id)
            return (
              <div key={id} className="rounded-lg bg-dark-bg-tertiary/50 p-3" data-testid="trupp-mitglied">
                <div className="mb-2 flex items-center gap-2 text-dark-text">
                  <span className="font-semibold">{p?.name ?? `Benutzer ${id}`}</span>
                  {id === trupp.fuehrerId && <span className="rounded bg-primary-600/25 px-1.5 py-0.5 text-[11px] font-semibold text-primary-200">TF</span>}
                </div>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Rolle von ${p?.name ?? id}`}>
                  {ROLLEN.map(r => {
                    const darf = (p?.funktionen ?? []).includes(r.funktion)
                    const aktiv = zuordnung[id] === r.feld
                    return (
                      <button key={r.feld} type="button" role="radio" aria-checked={aktiv} disabled={!darf} onClick={() => setzen(id, r.feld)}
                        data-testid={`rolle-${id}-${r.feld}`}
                        title={darf ? undefined : `Funktion ${r.label} fehlt`}
                        className={`min-h-[44px] rounded-lg border px-3 text-sm ${aktiv ? 'border-primary-500 bg-primary-600 font-semibold text-white' : 'border-dark-border bg-dark-bg-tertiary text-dark-text'} ${darf ? '' : 'cursor-not-allowed opacity-40'}`}>
                        {r.label}{darf ? '' : ' (Funktion fehlt)'}
                      </button>
                    )
                  })}
                  <button type="button" role="radio" aria-checked={zuordnung[id] == null} onClick={() => setzen(id, null)} data-testid={`rolle-${id}-keine`}
                    className={`min-h-[44px] rounded-lg border px-3 text-sm ${zuordnung[id] == null ? 'border-primary-500 bg-primary-600/40 font-semibold text-white' : 'border-dark-border bg-dark-bg-tertiary text-dark-text'}`}>
                    keine Rolle
                  </button>
                </div>
              </div>
            )
          })}
          {beobachterBleibt && (
            <p className="rounded-lg border border-dark-border bg-dark-bg-tertiary/40 px-3 py-2 text-sm text-dark-text-muted" data-testid="beobachter-bleibt">
              Luftraumbeobachter bleibt unverändert: <strong className="text-dark-text">{beobachterName}</strong>{beobachterAusserhalb ? ' (außerhalb des Trupps)' : ''}.
            </p>
          )}
          {!pilotId && trupp && trupp.mitglieder.length > 0 && <p className="text-sm text-amber-300">Bitte einen Pilot wählen.</p>}
          {fehler && <p role="alert" className="text-sm text-red-300" data-testid="trupp-fehler">{fehler}</p>}
        </div>
      </Dialog>, document.body)}
    </>
  )
}
